import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import AdminPage from '../../shared/AdminPage'
import {
  useModifyAdvisorPermissionGroupMutation,
  useModifyWealthOwnerPermissionGroupMutation,
  useSearchPermissionGroups,
  useSearchPermissions
} from '../../../../../api/users'
import Loading from '../../../../molecules/Loading'
import SaveCancelFooter from '../../shared/SaveCancelFooter'
import { useCurrentUser } from '../../../../../hooks'
import PermissionGroupBulkPermissions from './PermissionGroupBulkPermissions'
import PermissionGroupDetailSection from './PermissionGroupDetailSection'
import PermissionGroupSelectedPermissions from './PermissionGroupSelectedPermissions'
const PermissionGroupDetails = () => {
  const params = useParams()
  const queryEq = {
    includes: {
      permissions: true
    },
    filters: {
      permissionGroupId: [{ op: 'eq', value: params.permissionGroupId }]
    }
  }
  const [editing, setEditing] = React.useState(false)
  const { data: permissionGroupData, isLoading } = useSearchPermissionGroups(queryEq)
  const permissionGroup = React.useMemo(() => {
    return permissionGroupData?.[0] || { permissions: [] }
  }, [permissionGroupData])
  const { mutateAsync: updateWOPermissionGroup, isLoading: updateWOLoading } = useModifyWealthOwnerPermissionGroupMutation()
  const { mutateAsync: updateAdvisorPermissionGroup, isLoading: updateAdvisorLoading } = useModifyAdvisorPermissionGroupMutation()
  const { data: unassignedPermissionsData, isLoading: unassignedLoading } = useSearchPermissions({})
  const [pendingSelectList, setPendingSelectList] = React.useState([])
  const [search, setSearch] = React.useState('')
  const scrollRef = React.useRef(null)
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const canEditInternalPermissions = (permission) => {
    if (!permission.defaultToAdmins) {
      return user.internal
    } else {
      return true
    }
  }
  const handleReset = () => {
    setPendingSelectList(permissionGroup.permissions)
    setEditing(false)
    setSearch('')
    form.reset({
      permissionGroupId: params?.permissionGroupId || '',
      name: permissionGroup?.name || '',
      description: permissionGroup?.description || ''
    })
  }
  useEffect(() => {
    setPendingSelectList(permissionGroup.permissions)
  }, [permissionGroup])
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      permissionGroupId: params?.permissionGroupId || '',
      name: '',
      description: ''
    }
  })
  useEffect(() => {
    form.reset({
      permissionGroupId: params?.permissionGroupId || '',
      name: permissionGroup?.name || '',
      description: permissionGroup?.description || ''
    })
  }, [form, params?.permissionGroupId, permissionGroup])
  const selectPermission = (permissionId) => {
    const selected = unassignedPermissionsData?.find(p => p.permissionId === permissionId)
    if (!selected) return

    setPendingSelectList(prev => {
      const alreadySelected = prev.some(p => p.permissionId === permissionId)
      if (alreadySelected) return prev

      return [...prev, selected]
    })
  }
  const unselectPermission = (permissionId) => {
    setPendingSelectList(prev => {
      return prev.filter(p => p.permissionId !== permissionId)
    })
  }
  const filteredPermissions = React.useMemo(() => {
    if (!unassignedPermissionsData) return []
    if (!search) return unassignedPermissionsData
    const searchLower = search.toLowerCase()
    return unassignedPermissionsData.filter(p =>
      p.name.toLowerCase().includes(searchLower)
    )
  }, [unassignedPermissionsData, search])
  const isSelected = (permissionId) => {
    return pendingSelectList?.some(p => p.permissionId === permissionId)
  }
  const onSubmit = async (data) => {
    const command = {
      permissionGroupId: params?.permissionGroupId,
      name: data.name,
      description: data.description,
      assignableToUserType: permissionGroup?.assignableToUserType,
      permissions: pendingSelectList.map(p => {
        return {
          permissionId: p.permissionId,
          enabled: true
        }
      })
    }
    const result = permissionGroup.assignableToUserType === 1 ? await updateWOPermissionGroup(command) : await updateAdvisorPermissionGroup(command)
    if (result) {
      handleReset()
    }
  }
  if (isLoading || unassignedLoading || userLoading) { return <Loading /> }
  return (
    <AdminPage>
      <PermissionGroupDetailSection
        form={form}
        editing={editing}
        setEditing={setEditing}
        handleReset={handleReset}
      />
      <hr />
      <div className='flex flex-row sm:flex-col-reverse items-start w-full gap-lg'>
        <PermissionGroupBulkPermissions
          fullPermissionList={filteredPermissions}
          isSelected={isSelected}
          selectPermission={selectPermission}
          unselectPermission={unselectPermission}
          editing={editing}
          search={search}
          setSearch={setSearch}
          scrollRef={scrollRef}
          isInternal={user.internal}
          canEditInternalPermissions={canEditInternalPermissions}
          userLoading={userLoading}
          userType={permissionGroup?.assignableToUserType}
        />
        <PermissionGroupSelectedPermissions
          pendingSelectList={pendingSelectList}
          canEditInternalPermissions={canEditInternalPermissions}
          editing={editing}
          unselectPermission={unselectPermission}
        />
      </div>
      {
        editing && (
          <SaveCancelFooter processing={updateAdvisorLoading || updateWOLoading} onCancel={() => handleReset()} onSave={form.handleSubmit((data) => onSubmit(data))} />
        )
      }
    </AdminPage>
  )
}

export default PermissionGroupDetails
